import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "../../../lib/api/client"
import { changelogKeys } from "../../../lib/query-keys"
import { extractArray, extractTotal } from "../../../lib/api/response-utils"
import type { ChangelogEntry, ChangelogEntryType } from "../types"

interface UseChangelogEntriesParams {
  skip?: number
  limit?: number
  type?: ChangelogEntryType
  searchString?: string
}

export function useChangelogEntries(params: UseChangelogEntriesParams = {}) {
  const { skip = 0, limit = 20, type, searchString } = params

  return useQuery({
    queryKey: changelogKeys.list({ skip, limit, type, searchString }),
    queryFn: async () => {
      const response = await api.changelogEntries.search({
        skip,
        limit,
        searchString,
        type,
      })
      return {
        entries: extractArray<ChangelogEntry>(response),
        total: extractTotal(response),
      }
    },
  })
}

export function useChangelogUnreadCount() {
  return useQuery({
    queryKey: changelogKeys.unreadCount(),
    queryFn: async () => {
      try {
        const result = await api.changelogEntries.getUnreadCount()
        return result.data?.count ?? 0
      } catch {
        return 0
      }
    },
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 60,
  })
}

export function useMarkChangelogViewed() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.changelogEntries.markViewed(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: changelogKeys.unreadCount() })
    },
  })
}
